import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatAud } from "../../lib/api";
import StoreHeader from "../../components/layout/StoreHeader";
import StoreFooter from "../../components/layout/StoreFooter";
import { CheckCircle2, Clock, XCircle } from "lucide-react";

export default function OrderSuccess() {
  const [params] = useSearchParams();
  const session_id = params.get("session_id");
  const order_number = params.get("order_number");
  const [state, setState] = useState("checking");
  const [order, setOrder] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    // COD flow — server already confirmed
    if (order_number && !session_id) {
      api.get(`/orders/by-number/${encodeURIComponent(order_number)}`)
        .then((r) => { setOrder(r.data); setState("cod"); })
        .catch(() => setState("error"));
      return;
    }
    if (!session_id) { setState("error"); return; }

    let cancelled = false;
    const poll = async () => {
      if (cancelled || attempts.current >= 8) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${session_id}`);
        if (data.payment_status === "paid") {
          setOrder(data.order); setState("paid"); return;
        }
        if (data.status === "expired") { setState("expired"); return; }
        setState("pending");
        setTimeout(poll, 2000);
      } catch {
        setState("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [session_id, order_number]);

  return (
    <div className="bg-white min-h-screen" data-testid="order-success-page">
      <StoreHeader />
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-16">
        {state === "checking" || state === "pending" ? (
          <div className="text-center" data-testid="order-pending">
            <Clock className="h-10 w-10 mx-auto text-zinc-500" />
            <div className="mt-4 font-heading text-2xl font-black uppercase">Confirming payment…</div>
            <p className="text-zinc-500 mt-2">Hold tight while we verify with Stripe.</p>
          </div>
        ) : state === "paid" || state === "cod" ? (
          <div data-testid="order-confirmed">
            <CheckCircle2 className="h-12 w-12 text-green-600" />
            <div className="mt-4 text-red-600 font-mono text-xs uppercase tracking-[0.3em]">Order {state === "cod" ? "placed" : "paid"}</div>
            <h1 className="font-heading text-4xl font-black uppercase tracking-tight text-zinc-950 mt-1">Thank you</h1>
            <p className="text-zinc-600 mt-3">
              We've received your order {order?.order_number && <span className="font-mono">#{order.order_number}</span>}. A confirmation email is on its way.
            </p>

            {order && (
              <div className="mt-8 border border-zinc-200 rounded-sm">
                <div className="p-4 border-b border-zinc-200 flex justify-between font-mono text-xs uppercase tracking-widest text-zinc-500">
                  <span>Order #{order.order_number}</span>
                  <span>{order.payment_method.toUpperCase()} · {order.payment_status}</span>
                </div>
                <div className="divide-y divide-zinc-200">
                  {order.items.map((i) => (
                    <div key={i.sku} className="flex gap-4 p-4 items-center">
                      <div className="w-14 h-14 bg-zinc-100 overflow-hidden">
                        {i.image_url && <img src={i.image_url} alt={i.name} className="w-full h-full object-cover" />}
                      </div>
                      <div className="flex-1">
                        <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">{i.sku}</div>
                        <div className="font-heading font-bold text-sm">{i.name}</div>
                      </div>
                      <div className="font-mono text-sm">{i.qty} × {formatAud(i.price)}</div>
                    </div>
                  ))}
                </div>
                <div className="p-4 border-t border-zinc-200 space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-zinc-600">Subtotal</span><span>{formatAud(order.subtotal)}</span></div>
                  <div className="flex justify-between text-zinc-400 text-xs"><span>GST included (10%)</span><span>({formatAud(order.tax)})</span></div>
                  <div className="flex justify-between"><span className="text-zinc-600">Shipping</span><span>{order.shipping === 0 ? "FREE" : formatAud(order.shipping)}</span></div>
                  {order.insurance && order.insurance_amount > 0 && (
                    <div className="flex justify-between text-green-700">
                      <span>Transit insurance</span>
                      <span>+{formatAud(order.insurance_amount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 mt-2 border-t border-zinc-200 font-heading font-black"><span>Total</span><span>{formatAud(order.total)}</span></div>
                </div>
              </div>
            )}

            <Link to="/shop" className="inline-flex mt-8 h-11 px-6 bg-red-600 hover:bg-red-700 text-white font-heading uppercase tracking-wide font-bold text-sm items-center rounded-sm">
              Continue shopping
            </Link>
          </div>
        ) : (
          <div className="text-center" data-testid="order-error">
            <XCircle className="h-10 w-10 mx-auto text-red-600" />
            <div className="mt-4 font-heading text-2xl font-black uppercase">
              {state === "expired" ? "Session expired" : "Something went wrong"}
            </div>
            <p className="text-zinc-500 mt-2">Please try again or contact trade support if you were charged.</p>
            <Link to="/cart" className="inline-flex mt-6 h-11 px-6 bg-zinc-950 text-white font-heading uppercase tracking-wide font-bold text-sm items-center rounded-sm">
              Return to cart
            </Link>
          </div>
        )}
      </div>
      <StoreFooter />
    </div>
  );
}